import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCampaignsApi } from 'src/modules/campaigns/api';
import { useCampaignConfigService } from '../services';
import { useCampaignConfigLoader } from './useCampaignConfigLoader';
import { CampaignConfigSchema, CampaignConfigStatus } from '../types';
import { toSchema, toBackendConfig, toApplicationIds, isConfigValid, getValidationErrors } from '../helpers';

interface UseCampaignConfigLoadSaveArgs {
  configId?: string;
  config: CampaignConfigSchema;
  setConfig: React.Dispatch<React.SetStateAction<CampaignConfigSchema>>;
}

/**
 * Loads an existing config (edit) and saves create/update through the existing
 * campaign-builder service. Backend <-> schema conversion is delegated to the
 * pure transform helpers.
 */
export const useCampaignConfigLoadSave = ({ configId, config, setConfig }: UseCampaignConfigLoadSaveArgs) => {
  const navigate = useNavigate();
  const { getCampaignConfig, createCampaignConfig, updateCampaignConfig } = useCampaignConfigService();
  const { loadCampaignConfigs } = useCampaignConfigLoader();
  const { getCampaigns } = useCampaignsApi();

  const [isLoading, setIsLoading] = useState(!!configId);
  const [isSaving, setIsSaving] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [configStatus, setConfigStatus] = useState<CampaignConfigStatus>('active');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!configId) return;
      setIsLoading(true);
      try {
        const configData = await getCampaignConfig(configId);
        if (configData && !cancelled) {
          const { config: loaded, status } = toSchema(configData, new Date().toISOString());
          setConfig(loaded);
          setConfigStatus(status);
        }
      } catch (error) {
        console.error('Failed to load campaign config:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [configId, getCampaignConfig]);

  const isValid = isConfigValid(config);
  const validationErrors = getValidationErrors(config);

  const countCampaignsUsingConfig = useCallback(async () => {
    if (!configId) return 0;
    try {
      const response = await getCampaigns({
        // Backend treats the literal string 'null' as "all companies"; a real
        // null would make axios drop the param entirely.
        companyId: 'null',
        configId,
        status: 'active,draft',
        // Only the total count is used, so keep the page as small as possible.
        limit: 1,
        offset: 0,
      });
      return response.data.count;
    } catch (error) {
      console.error('Failed to count campaigns using config:', error);
      return 0;
    }
  }, [configId, getCampaigns]);

  const save = useCallback(async () => {
    if (!isValid) {
      setShowValidation(true);
      return;
    }
    setIsSaving(true);
    try {
      const backendConfig = toBackendConfig(config, new Date().toISOString());
      const applicationIds = toApplicationIds(config);

      if (configId) {
        await updateCampaignConfig(configId, { config: backendConfig, status: configStatus, applicationIds });
      } else {
        await createCampaignConfig({ config: backendConfig, applicationIds });
      }

      await loadCampaignConfigs(true);
      navigate('/admin/campaigns');
    } catch (error) {
      console.error('Failed to save campaign config:', error);
    } finally {
      setIsSaving(false);
    }
  }, [config, configId, configStatus, isValid, createCampaignConfig, updateCampaignConfig, loadCampaignConfigs, navigate]);

  return { isLoading, isSaving, showValidation, configStatus, isValid, validationErrors, save, countCampaignsUsingConfig };
};
